import { FundWalletOnSupply } from '@/components/opportunity/fundWalletOnSupply'
import type { TabConfig, TabsConfig } from '@/components/opportunity/useTabsConfig'
import type { useTxSuccessState } from '@/components/opportunity/useTxSuccessState'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { useEffect, useRef } from 'react'
import type { Address } from 'viem'
import { ActionTab } from '../actionTab/actionTab'
import { AeraPendingTab } from '../actionTab/aeraPendingTab'
import { BoringWaitForRedeemTab } from '../actionTab/boringWaitForRedeemTab'
import { ClaimRewardsExceptionTab } from '../actionTab/claimRewardsExceptionTab'
import { DepositActionContainer } from '../actionTab/depositActionContainer'
import {
  GenericPendingTab,
  type hasPendingRequestsField,
  type parsePendingRequests,
} from '../actionTab/genericPendingTab'
import { LIDO_WRAP_TAB_NAME, LidoWrappingTab } from '../actionTab/lidoWrappingTab'
import { UMBRELLA_WITHDRAW_TAB_NAME, UmbrellaWithdrawTab } from '../actionTab/umbrellaWithdrawTab'
import { YoWaitForRedeemTab } from '../actionTab/yoWaitForRedeemTab'
import { TxLoader } from './txLoader'
import { TxSuccessMessage } from './txSuccessMessage'

type TxSuccessState = ReturnType<typeof useTxSuccessState>

const umbrellaHiddenTabs: Array<keyof TabsConfig> = ['start-redeem-cooldown', 'claim-redeem']

interface TxActionExceptionFlags {
  isExternalClaimAvailable: boolean
  isLidoException: boolean
  isBoringException: boolean
  isUmbrellaException: boolean
  isYoException: boolean
  isAeraException: boolean
  hasGenericPending: ReturnType<typeof hasPendingRequestsField>
  showGenericPendingTab: boolean
  genericPendingRequests: ReturnType<typeof parsePendingRequests>
}

interface TxActionContentProps {
  account: Address | undefined
  vault: Vault
  transactionsContext: NonNullable<TransactionsContext>
  tabsConfig: TabsConfig
  activeConfig: TabConfig
  txState: 'idle' | 'loading' | 'success'
  txData: TxSuccessState['txData']
  resetTxState: TxSuccessState['resetTxState']
  setTransactionFinished: TxSuccessState['setTransactionFinished']
  isRedirectCancelled: boolean
  cancelRedirect: () => void
  goToDashboard: () => void
  displayFundWallet: boolean
  setDisplayFundWallet: (value: boolean) => void
  canShowCrosschain: boolean
  exceptions: TxActionExceptionFlags
}

export const TxActionContent = ({
  account,
  vault,
  transactionsContext,
  tabsConfig,
  activeConfig,
  txState,
  txData,
  resetTxState,
  setTransactionFinished,
  isRedirectCancelled,
  cancelRedirect,
  goToDashboard,
  displayFundWallet,
  setDisplayFundWallet,
  canShowCrosschain,
  exceptions,
}: TxActionContentProps) => {
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const { asset, network } = vault
  const {
    isExternalClaimAvailable,
    isLidoException,
    isBoringException,
    isUmbrellaException,
    isYoException,
    isAeraException,
    hasGenericPending,
    showGenericPendingTab,
    genericPendingRequests,
  } = exceptions

  const setTransactionFinishedAndReset: TxSuccessState['setTransactionFinished'] = (args) => {
    if (!mounted.current) return
    setTransactionFinished(args)
    activeConfig.form.reset()
  }

  return (
    <>
      <TxLoader className={cn(txState !== 'loading' && 'hidden')} />

      <TxSuccessMessage
        activeConfig={activeConfig}
        amount={txData.amount}
        vaultNetwork={network.name}
        txHash={txData.txHash}
        resetTxState={resetTxState}
        isRedirectCancelled={isRedirectCancelled}
        cancelRedirect={cancelRedirect}
        goToDashboard={goToDashboard}
        className={cn(txState !== 'success' && 'hidden')}
      />

      <FundWalletOnSupply
        depositConfig={tabsConfig.deposit}
        open={displayFundWallet}
        network={network.name}
        asset={asset}
        walletBalance={tabsConfig.deposit.walletBalance}
        closeModal={() => setDisplayFundWallet(false)}
      />

      {transactionsContext.allSteps.map((step) => {
        if (isUmbrellaException && umbrellaHiddenTabs.includes(step.name)) return null
        if (hasGenericPending && (step.name === 'claim-deposit' || step.name === 'claim-redeem')) return null

        if (step.name === 'deposit') {
          return (
            <DepositActionContainer
              account={account}
              key={step.name}
              stepData={step}
              tabConfig={tabsConfig[step.name]}
              setDisplayFundWallet={setDisplayFundWallet}
              transactionsContext={transactionsContext}
              setTransactionFinished={setTransactionFinishedAndReset}
              txState={txState}
              canShowCrosschain={canShowCrosschain}
            />
          )
        }

        return (
          <ActionTab
            account={account}
            key={step.name}
            stepData={step}
            tabConfig={tabsConfig[step.name]}
            setDisplayFundWallet={setDisplayFundWallet}
            transactionsContext={transactionsContext}
            setTransactionFinished={setTransactionFinishedAndReset}
            txState={txState}
          />
        )
      })}
      {isExternalClaimAvailable && <ClaimRewardsExceptionTab vault={vault} />}
      {isLidoException && (
        <LidoWrappingTab
          account={account}
          vault={vault}
          tabConfig={tabsConfig[LIDO_WRAP_TAB_NAME]}
          setTransactionFinished={setTransactionFinishedAndReset}
          txState={txState}
        />
      )}
      {isBoringException && <BoringWaitForRedeemTab vault={vault} transactionsContext={transactionsContext} />}
      {isUmbrellaException && (
        <UmbrellaWithdrawTab
          account={account}
          vault={vault}
          tabConfig={tabsConfig[UMBRELLA_WITHDRAW_TAB_NAME]}
          txState={txState}
          transactionsContext={transactionsContext}
          setTransactionFinished={setTransactionFinished}
        />
      )}
      {isYoException && <YoWaitForRedeemTab vault={vault} transactionsContext={transactionsContext} />}
      {isAeraException && <AeraPendingTab vault={vault} transactionsContext={transactionsContext} />}
      {showGenericPendingTab && (
        <GenericPendingTab
          account={account}
          vault={vault}
          pendingRequests={genericPendingRequests}
          setTransactionFinished={setTransactionFinishedAndReset}
          txState={txState}
        />
      )}
    </>
  )
}
